import { useEffect } from "react";
import type { SEOProps } from "@/types";
import ogImageFile from "@/assets/ville-kaisla-HNCSCpWrVJA-unsplash.jpg";

const SEO: React.FC<SEOProps> = ({
	title = "WILDFÖRSTER - Professionelle Fahrzeugaufbereitung in Wuppertal",
	description = "Professionelle Fahrzeugaufbereitung in Wuppertal. Autopflege, Lackversiegelung, Innenraumreinigung und mehr. Jetzt Kontakt aufnehmen!",
	keywords = "Fahrzeugaufbereitung, Autopflege, Wuppertal, Lackversiegelung, Keramikversiegelung, Innenraumreinigung, Polieren, Autoaufbereitung",
	ogImage = ogImageFile,
	ogType = "website",
	canonical = "https://aufbereitung-wuppertal.de/",
	schema = null,
}) => {
	const siteName = "WILDFÖRSTER Fahrzeugaufbereitung";
	const fullTitle = title.includes("WILDFÖRSTER")
		? title
		: `${title} | WILDFÖRSTER`;

	useEffect(() => {
		// Set title
		document.title = fullTitle;

		// Helper function to set or update meta tag
		const setMeta = (name: string, content: string, property = false) => {
			const attr = property ? "property" : "name";
			let element = document.querySelector(`meta[${attr}="${name}"]`);
			
			if (!element) {
				element = document.createElement("meta");
				element.setAttribute(attr, name);
				document.head.appendChild(element);
			}
			
			element.setAttribute("content", content);
		};

		// Helper function to set or update link tag
		const setLink = (rel: string, href: string) => {
			let element = document.querySelector(`link[rel="${rel}"]`);
			
			if (!element) {
				element = document.createElement("link");
				element.setAttribute("rel", rel);
				document.head.appendChild(element);
			}
			
			element.setAttribute("href", href);
		};

		// Basic Meta Tags
		setMeta("description", description);
		setMeta("keywords", keywords);
		setLink("canonical", canonical);

		// Robots
		setMeta("robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
		setMeta("googlebot", "index, follow");

		// Open Graph / Facebook
		setMeta("og:type", ogType, true);
		setMeta("og:site_name", siteName, true);
		setMeta("og:title", fullTitle, true);
		setMeta("og:description", description, true);
		setMeta("og:url", canonical, true);
		setMeta("og:image", ogImage, true);
		setMeta("og:image:width", "1200", true);
		setMeta("og:image:height", "630", true);
		setMeta("og:locale", "de_DE", true);

		// Twitter
		setMeta("twitter:card", "summary_large_image");
		setMeta("twitter:title", fullTitle);
		setMeta("twitter:description", description);
		setMeta("twitter:image", ogImage);

		// Mobile
		setMeta("viewport", "width=device-width, initial-scale=1.0, maximum-scale=5.0");
		setMeta("theme-color", "#000000");

		// Author
		setMeta("author", "Daniel Wildförster");
		setMeta("copyright", "Daniel Wildförster");

		// Geo Tags
		setMeta("geo.region", "DE-NW");
		setMeta("geo.placename", "Wuppertal");
		setMeta("geo.position", "51.2562;7.1508");
		setMeta("ICBM", "51.2562, 7.1508");

		// Schema.org JSON-LD
		if (schema !== null) {
			let schemaScript = document.querySelector('script[type="application/ld+json"]');
			
			if (!schemaScript) {
				schemaScript = document.createElement("script");
				schemaScript.setAttribute("type", "application/ld+json");
				document.head.appendChild(schemaScript);
			}
			
			schemaScript.textContent = JSON.stringify(schema);
		}
	}, [fullTitle, description, keywords, ogImage, ogType, canonical, schema, siteName]);

	return null;
};

export default SEO;
